import type { AddressBookVariable } from '@integrations/address-books';
import type { FormControlType } from '@common/types/form-element.type';
import type { CrmDeal } from '@integrations/crm-deals';
import type { CrmContact } from '@integrations/crm-contacts';

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { AsyncPipe } from '@angular/common';

import { environment } from '@web-env/environment';

import type { FormWidgetElement } from '@sites/entity-core';
import type { PaymentFormWidgetForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpFormElementSettingsSignalComponent } from '@components/sp-form-element-settings-signal';
import { FormSettingsPanel } from '@web/widgets/common/abstract/form-settings-panel';

/**
 * Breadcrumbs/footer chrome around `sp-form-element-settings-signal`, editing
 * one element of the payment-form's `elements` array in place
 * (`form.updateElement(index, …)`). Payment forms have no chat-bot fields, so
 * the chat-bot inputs/outputs of the form widget's element panel are omitted.
 * Save/cancel and the transaction frame come from `FormSettingsPanel`.
 */
@Component({
    selector: 'web-payment-element-settings',
    templateUrl: './payment-element-settings.component.html',
    styleUrls: ['./payment-element-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormElementSettingsSignalComponent, AsyncPipe],
})
export class PaymentElementSettingsComponent extends FormSettingsPanel<PaymentFormWidgetForm> {
    public readonly index = input.required<number>();

    public readonly addressBookVariablesList = input<AddressBookVariable[]>([]);
    public readonly crmContactsVariablesList = input<CrmContact[]>([]);
    public readonly crmDealsVariablesList = input<CrmDeal[]>([]);
    public readonly filteredFormControls = input<FormControlType[]>([]);

    public readonly isFormControlDisabled = input<boolean>(false);
    public readonly isCrmDealsListDisabled = input<boolean>(false);
    public readonly isCrmContactListDisabled = input<boolean>(false);
    public readonly isAddressBookListDisabled = input<boolean>(false);
    public readonly isPaymentEduForm = input<boolean>(false);

    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    public readonly baseHref = environment.baseHref;
    public readonly selectedDeviceMode$ = this.layoutFacade.deviceMode$;

    public readonly element = computed<FormWidgetElement | undefined>(
        () => this.form().elements().value()[this.index()]
    );
    public readonly elements = computed<FormWidgetElement[]>(() => this.form().elements().value());

    public onElementChange(value: FormWidgetElement): void {
        this.form().updateElement(this.index(), value);
    }
}
